import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Icon,
  IconButton,
  Grid,
  TextField,
  Divider,
  useTheme,
  alpha,
  styled,
} from "@mui/material";

interface DenominationCountProps {
  onSubmit: (data: {
    bills: Array<{ value: number; count: number }>;
    coins: Array<{ value: number; count: number }>;
  }) => void;
  initialData: {
    bills: Array<{ value: number; count: number }>;
    coins: Array<{ value: number; count: number }>;
  };
}

// Custom colors to match the system theme with better contrast
const THEME_COLORS = {
  primary: '#1B7B6B',  // Deep teal from header
  primaryLight: '#1E8078', // Lighter teal for hover states
  primaryDark: '#166F68', // Darker teal for active states
  success: '#059669',
  danger: '#DC2626',
  background: '#FFFFFF',
  text: {
    primary: '#1A202C',    // Darker text for better contrast
    secondary: '#4A5568',  // Mid-gray for secondary text
    muted: '#718096',     // Light gray for less important text
  }
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: alpha(THEME_COLORS.primary, 0.5),
    boxShadow: `0 4px 12px ${alpha(THEME_COLORS.primary, 0.08)}`,
  },
}));

const QuickActionButton = styled(Box)<{ isAdd: boolean }>(({ theme, isAdd }) => ({
  flex: 1,
  height: 36,
  backgroundColor: isAdd 
    ? alpha(THEME_COLORS.primary, 0.08)
    : alpha(THEME_COLORS.danger, 0.08),
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  userSelect: "none",
  color: isAdd ? THEME_COLORS.primary : THEME_COLORS.danger,
  transition: "all 0.2s ease-in-out",
  border: `1px solid ${isAdd 
    ? alpha(THEME_COLORS.primary, 0.3)
    : alpha(THEME_COLORS.danger, 0.2)}`,
  "&:hover": {
    backgroundColor: isAdd 
      ? alpha(THEME_COLORS.primary, 0.12)
      : alpha(THEME_COLORS.danger, 0.12),
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: THEME_COLORS.primary,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0, 0, 1, 0),
  borderBottom: `2px solid ${alpha(THEME_COLORS.primary, 0.1)}`,
  marginBottom: theme.spacing(1.5),
}));

export const DenominationCount: React.FC<DenominationCountProps> = ({
  onSubmit,
  initialData,
}) => {
  const theme = useTheme();
  const [bills, setBills] = useState(initialData.bills);
  const [coins, setCoins] = useState(initialData.coins);

  const handleCountChange = (
    type: "bills" | "coins",
    value: number,
    newCount: number
  ) => {
    const validCount = Math.max(0, newCount);
    if (type === "bills") {
      const newBills = bills.map((item) =>
        item.value === value ? { ...item, count: validCount } : item
      );
      setBills(newBills);
      onSubmit({ bills: newBills, coins });
    } else {
      const newCoins = coins.map((item) =>
        item.value === value ? { ...item, count: validCount } : item
      );
      setCoins(newCoins);
      onSubmit({ bills, coins: newCoins });
    }
  };

  const calculateTotal = (items: Array<{ value: number; count: number }>) => {
    return items.reduce((sum, item) => sum + item.value * item.count, 0);
  };

  const QuickAction = ({
    onClick,
    label,
    isAdd,
  }: {
    onClick: () => void;
    label: string;
    isAdd: boolean;
  }) => (
    <QuickActionButton onClick={onClick} isAdd={isAdd}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
        {isAdd ? `+${label}` : `-${label}`}
      </Typography>
    </QuickActionButton>
  );

  const DenominationCounter = ({
    value,
    count,
    type,
    label,
  }: {
    value: number;
    count: number;
    type: "bills" | "coins";
    label?: string;
  }) => {
    const quickAdd = (amount: number) => {
      handleCountChange(type, value, count + amount);
    };

    const quickRemove = (amount: number) => {
      handleCountChange(type, value, count - amount);
    };

    return (
      <StyledPaper elevation={0}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              flex: 1,
              color: THEME_COLORS.text.primary,
            }}
          >
            {type === "bills" ? `BD ${value}` : label}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: count > 0 ? THEME_COLORS.primary : THEME_COLORS.text.muted,
              minWidth: 48,
              textAlign: "right",
            }}
          >
            {count}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {type === "bills" ? (
            <>
              <QuickAction onClick={() => quickAdd(1)} label="1" isAdd={true} />
              <QuickAction onClick={() => quickAdd(5)} label="5" isAdd={true} />
              <QuickAction onClick={() => quickAdd(10)} label="10" isAdd={true} />
            </>
          ) : (
            <>
              <QuickAction
                onClick={() => handleCountChange("coins", value, count + 1)}
                label="1"
                isAdd={true}
              />
              <QuickAction
                onClick={() => handleCountChange("coins", value, count + 10)}
                label="10"
                isAdd={true}
              />
            </>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {type === "bills" ? (
            <>
              <QuickAction onClick={() => quickRemove(1)} label="1" isAdd={false} />
              <QuickAction onClick={() => quickRemove(5)} label="5" isAdd={false} />
              <QuickAction onClick={() => quickRemove(10)} label="10" isAdd={false} />
            </>
          ) : (
            <>
              <QuickAction
                onClick={() => handleCountChange("coins", value, Math.max(0, count - 1))}
                label="1"
                isAdd={false}
              />
              <QuickAction
                onClick={() => handleCountChange("coins", value, Math.max(0, count - 10))}
                label="10"
                isAdd={false}
              />
            </>
          )}
        </Box>
      </StyledPaper>
    );
  };

  const grandTotal = calculateTotal(bills) + calculateTotal(coins);

  const getCoinLabel = (value: number) => {
    if (value === 0.5) return "500 fils";
    if (value === 0.1) return "100 fils";
    if (value === 0.05) return "50 fils";
    if (value === 0.025) return "25 fils";
    return "";
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Paper Currency Section */}
        <Grid item xs={12} md={8}>
          <Box>
            <SectionTitle variant="h6">
              <Icon>payments</Icon>
              Paper Currency
            </SectionTitle>
            <Grid container spacing={1.5}>
              {bills.map((bill) => (
                <Grid item xs={12} sm={6} lg={3} key={bill.value}>
                  <DenominationCounter
                    value={bill.value}
                    count={bill.count}
                    type="bills"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Coins Section */}
          <Box sx={{ mt: 2 }}>
            <SectionTitle variant="h6">
              <Icon fontSize="small">paid</Icon>
              Coins
            </SectionTitle>
            <Grid container spacing={1.5}>
              {coins.map((coin) => (
                <Grid item xs={12} sm={6} lg={3} key={coin.value}>
                  <DenominationCounter
                    value={coin.value}
                    count={coin.count}
                    type="coins"
                    label={getCoinLabel(coin.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        {/* Totals Column */}
        <Grid item xs={12} md={4}>
          <StyledPaper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: alpha(THEME_COLORS.primary, 0.04),
              border: `2px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              "&:hover": {
                borderColor: alpha(THEME_COLORS.primary, 0.3),
                boxShadow: `0 4px 20px ${alpha(THEME_COLORS.primary, 0.12)}`,
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.primary,
                display: "flex",
                alignItems: "center",
                gap: 1,
                pb: 1.5,
                borderBottom: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
              }}
            >
              <Icon>summarize</Icon>
              Summary
            </Typography>

            <Grid container spacing={2} sx={{ mt: 0.5, flex: 1 }}>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Paper Currency
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: THEME_COLORS.primary,
                      fontWeight: 600,
                    }}
                  >
                    BD {calculateTotal(bills).toFixed(3)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Coins
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: THEME_COLORS.primary,
                      fontWeight: 600,
                    }}
                  >
                    BD {calculateTotal(coins).toFixed(3)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ 
              mt: 'auto',
              pt: 2,
              borderTop: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
            }}>
              <Typography
                variant="body2"
                sx={{ 
                  color: THEME_COLORS.text.secondary,
                  mb: 0.5,
                }}
              >
                Total Amount
              </Typography>
              <Typography
                variant="h4"
                sx={{ 
                  fontWeight: 700, 
                  color: THEME_COLORS.primary,
                  lineHeight: 1.2,
                }}
              >
                BD {grandTotal.toFixed(3)}
              </Typography>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>
    </Box>
  );
};
